import {
  BOOKING_PAYMENT_STATUSES,
  BOOKING_SESSION_STATUS,
  BOOKING_STATUSES,
  VISIT_EVENT_TYPES,
} from 'src/consumer/constants'
import { MUIThemeConfig } from 'src/context/MUIThemeConfig'

/**
 * Calculate the best text color (black or white) based on background color luminance
 * @param {string} backgroundColor - Hex color string (e.g., '#E8E9EA')
 * @returns {string} - '#000000' for light backgrounds, '#FFFFFF' for dark backgrounds
 */
function getContrastTextColor(backgroundColor) {
  if (!backgroundColor) return '#1F2937' // Default dark gray
  
  // Remove # if present
  const hex = backgroundColor.replace('#', '')
  
  // Parse RGB values
  const r = parseInt(hex.substring(0, 2), 16)
  const g = parseInt(hex.substring(2, 4), 16)
  const b = parseInt(hex.substring(4, 6), 16)
  
  // Calculate relative luminance using WCAG formula
  const getLuminance = (val) => {
    val = val / 255
    return val <= 0.03928 ? val / 12.92 : Math.pow((val + 0.055) / 1.055, 2.4)
  }
  
  const luminance = 0.2126 * getLuminance(r) + 0.7152 * getLuminance(g) + 0.0722 * getLuminance(b)
  
  // Return white for dark backgrounds (luminance < 0.5), black for light backgrounds
  return luminance < 0.5 ? '#FFFFFF' : '#1F2937'
}

export function getBookedSlotStripColor(bookedSlot, slotBlocked = false, showAsNormalForMentorOrSupport = false) {
  const returnObj = {
    // Default scheduled - Softer gray body, brighter indigo border
    background: '#E8E9EA', // Light, soothing gray
    borderColor: '#6366F1', // Bright indigo border
    leftStripColor: MUIThemeConfig.palette.emrPrimary.main,
    leftStripBorderColor: MUIThemeConfig.palette.emrPrimaryLightBackground.main,
    visitTextColor: '#4338CA', // Dark indigo for legibility
    fontColor: '#1F2937', // Dark gray for clear legibility on light background
    leftStripTextColor: '#FFFFFF', // Default white for left strip (usually dark purple)

    inTherapyColor: '#6779E3',
    inTherapyTextColor: '#fff',
    inTherapyBorderColor: '#d2d6f1',

    // Event type + steps strip (Assessment, Consultation, etc.) – distinct from therapy strip
    eventTypeStripColor: '#0D9488', // Teal
    eventTypeStripTextColor: '#fff',
    eventTypeStripBorderColor: '#14B8A6',

    consultsTableColor: 'inherit',
    isOtherClinic: false, // Flag to indicate this is from another clinic
  }
  
  // Check if this is a slot from another clinic (in support portal or mentor view)
  // slotBlocked = true means it's from a different clinic
  // showAsNormalForMentorOrSupport = true means we should show it normally (not blocked)
  const isOtherClinicCard = slotBlocked && showAsNormalForMentorOrSupport
  
  if (isOtherClinicCard) {
    returnObj.isOtherClinic = true
    // Distinct colors for other clinic cards - Light lavender/purple theme
    returnObj.background = '#F3F4F6' // Very light gray with purple tint
    returnObj.borderColor = '#8B5CF6' // Bright purple/violet border
    returnObj.leftStripColor = '#E9D5FF' // Light purple strip
    returnObj.leftStripBorderColor = '#8B5CF6' // Purple border
    returnObj.visitTextColor = '#6D28D9' // Dark purple for visit text
    returnObj.fontColor = '#4B5563' // Dark gray for text
    returnObj.leftStripTextColor = '#6D28D9' // Dark purple for strip text
  }

  // Check if this is a blocked event due to pending payment
  const isPaymentPending =
    bookedSlot?.status === BOOKING_STATUSES.BOOKING_INITIATED &&
    bookedSlot?.paymentStatus === BOOKING_PAYMENT_STATUSES.PENDING

  // Payment Pending - Show orange/amber theme for payment awaited
  // But preserve other clinic purple border if it's from another clinic
  if (isPaymentPending) {
    returnObj.background = '#FFF4E6' // Very light orange
    returnObj.borderColor = isOtherClinicCard ? '#8B5CF6' : '#FB923C' // Purple border for other clinic, orange otherwise
    returnObj.leftStripColor = '#FFEDD5' // Light orange strip
    returnObj.leftStripBorderColor = isOtherClinicCard ? '#8B5CF6' : '#FB923C' // Purple border for other clinic
    returnObj.fontColor = '#7C2D12' // Dark orange text
    returnObj.leftStripTextColor = '#7C2D12' // Dark orange for strip text
    returnObj.visitTextColor = '#EA580C' // Medium orange for visit text
    returnObj.consultsTableColor = '#FFF4E6'
    if (isOtherClinicCard) {
      returnObj.isOtherClinic = true
    }
    return returnObj
  }

  // For mentors and support portal, don't show blocked colors even if from different clinic
  if (slotBlocked && !showAsNormalForMentorOrSupport) {
    // Blocked - Dark but with bright border
    returnObj.background = '#1F1F1F' // Softer black
    returnObj.borderColor = '#FF6B6B' // Bright red border
    returnObj.leftStripColor = '#1F1F1F'
    returnObj.leftStripBorderColor = '#FF6B6B'
    returnObj.fontColor = getContrastTextColor(returnObj.background)
    returnObj.leftStripTextColor = getContrastTextColor(returnObj.leftStripColor)
    returnObj.visitTextColor = '#FFE5E5'
    return returnObj
  }

  // Check status first (markedCompleted, in progress) to determine body color
  // But preserve other clinic colors if already set
  if (bookedSlot?.markedCompleted && !isOtherClinicCard) {
    // Completed - Softer green body, bright green border
    returnObj.background = '#D1FAE5' // Soft, soothing mint green
    returnObj.borderColor = '#10B981' // Bright emerald green border
    returnObj.leftStripColor = '#A7F3D0' // Light green strip
    returnObj.leftStripBorderColor = '#10B981' // Bright green border
    returnObj.fontColor = getContrastTextColor(returnObj.background)
    returnObj.leftStripTextColor = getContrastTextColor(returnObj.leftStripColor)
    returnObj.visitTextColor = '#047857' // Darker green for visit text
    returnObj.consultsTableColor = '#D1FAE5'
  } else if (bookedSlot?.markedCompleted && isOtherClinicCard) {
    // Completed + Other Clinic - Green tint with purple border
    returnObj.background = '#D1FAE5' // Soft green
    returnObj.borderColor = '#8B5CF6' // Purple border to indicate other clinic
    returnObj.leftStripColor = '#A7F3D0' // Light green strip
    returnObj.leftStripBorderColor = '#8B5CF6' // Purple border
    returnObj.fontColor = getContrastTextColor(returnObj.background)
    returnObj.leftStripTextColor = getContrastTextColor(returnObj.leftStripColor)
    returnObj.visitTextColor = '#047857'
    returnObj.consultsTableColor = '#D1FAE5'
  } else if (bookedSlot?.sessionStatus === BOOKING_SESSION_STATUS.IN_PROGRESS && !isOtherClinicCard) {
    // In Progress - Softer yellow body, bright yellow border
    returnObj.background = '#FEF3C7' // Soft, soothing amber/yellow
    returnObj.borderColor = '#F59E0B' // Bright amber border
    returnObj.leftStripColor = '#FDE68A' // Light yellow strip
    returnObj.leftStripBorderColor = '#F59E0B' // Bright amber border
    returnObj.fontColor = getContrastTextColor(returnObj.background)
    returnObj.leftStripTextColor = getContrastTextColor(returnObj.leftStripColor)
    returnObj.visitTextColor = '#78350F' // Darker amber for visit text
    returnObj.consultsTableColor = '#FEF3C7'
  } else if (bookedSlot?.sessionStatus === BOOKING_SESSION_STATUS.IN_PROGRESS && isOtherClinicCard) {
    // In Progress + Other Clinic - Yellow tint with purple border
    returnObj.background = '#FEF3C7' // Soft yellow
    returnObj.borderColor = '#8B5CF6' // Purple border to indicate other clinic
    returnObj.leftStripColor = '#FDE68A' // Light yellow strip
    returnObj.leftStripBorderColor = '#8B5CF6' // Purple border
    returnObj.fontColor = getContrastTextColor(returnObj.background)
    returnObj.leftStripTextColor = getContrastTextColor(returnObj.leftStripColor)
    returnObj.visitTextColor = '#78350F'
    returnObj.consultsTableColor = '#FEF3C7'
  }

  // Emergency: Override border colors to red, but keep body colors from status above
  if (bookedSlot?.visitEventType === VISIT_EVENT_TYPES.emergency) {
    returnObj.borderColor = '#EF4444' // Bright red border for emergency
    returnObj.leftStripBorderColor = '#EF4444' // Bright red border for left strip
    // Keep background and other colors from status checks above
  }

  // Ensure default font color is calculated based on background
  returnObj.fontColor = getContrastTextColor(returnObj.background)
  returnObj.leftStripTextColor = getContrastTextColor(returnObj.leftStripColor)
  return returnObj
}

export function getBookedSlotStripColorForRoster(bookedSlot, slotBlocked = false) {
  const returnObj = {
    background: '#fff3b6',
    borderColor: '#FFE58F',
    fontColor: '#B58B00',
    consultsTableColor: 'inherit',
    emoji: '🕒',
  }

  if (bookedSlot?.hideSlot) {
    returnObj.background = '#000'
    returnObj.borderColor = '#000'
    returnObj.fontColor = '#fff'
    returnObj.emoji = '🔒'
    return returnObj
  }

  // Check if this is a blocked event due to pending payment
  const isPaymentPending =
    bookedSlot?.status === BOOKING_STATUSES.BOOKING_INITIATED &&
    bookedSlot?.paymentStatus === BOOKING_PAYMENT_STATUSES.PENDING

  // Payment Pending - Show orange theme
  if (isPaymentPending) {
    returnObj.background = '#FFF4E6'
    returnObj.borderColor = '#FB923C'
    returnObj.fontColor = '#7C2D12'
    returnObj.emoji = '💳'
    return returnObj
  }

  if (slotBlocked) {
    returnObj.background = '#FFD6D6'
    returnObj.borderColor = '#FFC9C9'
    returnObj.fontColor = '#C54646'
    returnObj.emoji = '💰'
    return returnObj
  }

  if (bookedSlot?.markedCompleted) {
    returnObj.background = '#DBF7E0'
    returnObj.borderColor = '#A8E8BB'
    returnObj.fontColor = '#3C9C5E'
    returnObj.emoji = '✅'
    // returnObj.leftStripColor = '#021a0e'
    // returnObj.leftStripBorderColor = 'rgb(38, 255, 0)'
    // returnObj.consultsTableColor = '#00e01957'
    return returnObj
  }

  return returnObj
}

